import { Link } from "react-router-dom";
import {
  AlertTriangle, Building2, LifeBuoy, MessageSquare, TrendingUp, Users, Wallet, Zap,
} from "lucide-react";
import { MasterShell } from "@/shared/layouts/MasterShell.jsx";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card.jsx";
import { Button } from "@/shared/ui/button.jsx";
import { Badge } from "@/shared/ui/badge.jsx";
import { LoadingState } from "@/shared/ui/spinner.jsx";
import { formatCurrency, formatNumber } from "@/shared/lib/format.js";
import { StatCard, ClientStatusBadge } from "./components/MasterUi.jsx";
import { useMasterStats, useMasterClients, useMasterTickets } from "./use-master.js";

/**
 * Visão geral da plataforma.
 *
 * Todos os números vêm de contagens reais no banco — MRR somado dos
 * contratos ativos, inadimplência das faturas em atraso, uso agregado das
 * clínicas. O painel antigo exibia constantes escritas no código.
 */
export function MasterDashboardPage() {
  const { data: stats, isLoading } = useMasterStats();
  const { data: clientes = [] } = useMasterClients();
  const { data: chamados = [] } = useMasterTickets();

  if (isLoading || !stats) {
    return (
      <MasterShell title="Dashboard Geral" subtitle="Visão consolidada da plataforma">
        <LoadingState message="Carregando indicadores..." />
      </MasterShell>
    );
  }

  const recentes = clientes.slice(0, 5);
  const chamadosAbertos = chamados.filter((t) => t.status !== "resolvido").slice(0, 5);

  return (
    <MasterShell title="Dashboard Geral" subtitle="Visão consolidada da plataforma">
      <div className="space-y-6 p-6">
        {/* --------------------------------------------------- receita */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <StatCard
            label="Receita recorrente (MRR)"
            value={formatCurrency(stats.clinics.mrr)}
            hint={`${stats.clinics.ativos} contratos ativos`}
            icon={TrendingUp}
            accent="success"
          />
          <StatCard
            label="Clientes"
            value={formatNumber(stats.clinics.total)}
            hint={`${stats.clinics.demonstracao} em demonstração`}
            icon={Building2}
            accent="primary"
          />
          <StatCard
            label="Em atraso"
            value={formatCurrency(stats.invoices.atrasado)}
            hint={`${stats.invoices.qtdAtrasadas} cobrança(s) vencida(s)`}
            icon={AlertTriangle}
            accent="destructive"
          />
          <StatCard
            label="A receber"
            value={formatCurrency(stats.invoices.aReceber)}
            hint={`${formatCurrency(stats.invoices.recebido)} já recebido`}
            icon={Wallet}
            accent="warning"
          />
        </div>

        {/* ------------------------------------------------------- uso */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Usuários na plataforma" value={formatNumber(stats.usage.usuarios)} icon={Users} accent="chart-2" />
          <StatCard label="Leads gerenciados" value={formatNumber(stats.usage.leads)} icon={TrendingUp} accent="chart-3" />
          <StatCard label="Mensagens trocadas" value={formatNumber(stats.usage.mensagens)} icon={MessageSquare} accent="chart-4" />
          <StatCard
            label="Jornadas ativas"
            value={formatNumber(stats.usage.jornadas)}
            hint={`${stats.usage.whatsappAtivos} clínica(s) com WhatsApp conectado`}
            icon={Zap}
            accent="chart-5"
          />
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          {/* ------------------------------------------ últimos clientes */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="font-display text-base">Clientes recentes</CardTitle>
              <Button asChild variant="ghost" size="sm">
                <Link to="/master/clientes">Ver todos</Link>
              </Button>
            </CardHeader>
            <CardContent className="space-y-2">
              {recentes.length === 0 ? (
                <p className="py-4 text-center text-sm text-muted-foreground">
                  Nenhum cliente cadastrado ainda.
                </p>
              ) : (
                recentes.map((cliente) => (
                  <div
                    key={cliente.id}
                    className="flex items-center justify-between gap-3 rounded-lg border border-border/60 p-3"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{cliente.tradeName}</p>
                      <p className="truncate text-xs text-muted-foreground">
                        {cliente.planName} · {cliente.metrics.users} usuário(s) ·{" "}
                        {cliente.metrics.leads} lead(s)
                      </p>
                    </div>
                    <ClientStatusBadge status={cliente.status} />
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* -------------------------------------------------- suporte */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div>
                <CardTitle className="font-display text-base">Suporte</CardTitle>
                <p className="text-xs text-muted-foreground">
                  {stats.support.abertos} aberto(s) · {stats.support.emAndamento} em andamento
                </p>
              </div>
              <Button asChild variant="ghost" size="sm">
                <Link to="/master/suporte">Ver todos</Link>
              </Button>
            </CardHeader>
            <CardContent className="space-y-2">
              {chamadosAbertos.length === 0 ? (
                <div className="flex flex-col items-center gap-2 py-6 text-muted-foreground">
                  <LifeBuoy className="h-8 w-8 opacity-40" />
                  <p className="text-sm">Nenhum chamado em aberto.</p>
                </div>
              ) : (
                chamadosAbertos.map((chamado) => (
                  <div
                    key={chamado.id}
                    className="flex items-center justify-between gap-3 rounded-lg border border-border/60 p-3"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{chamado.subject}</p>
                      <p className="truncate text-xs text-muted-foreground">
                        {chamado.clinicName} · {chamado.type}
                      </p>
                    </div>
                    <Badge variant="outline" className="shrink-0 capitalize">
                      {chamado.priority}
                    </Badge>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </MasterShell>
  );
}
